import { MenuItem, Select } from '@mui/material';
import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';

import {
  type FilterProperty,
  PropertyPolicyRelation,
} from '@/types/advanceFilter';

import { inlineDropdownStyle } from './inlineDropdownStyle';

export const OPERATORS: Record<
  PropertyPolicyRelation,
  Partial<Record<ConditionOperatorEnum, { label: string }>>
> = {
  [PropertyPolicyRelation.SingleValue]: {
    [ConditionOperatorEnum.In]: { label: 'is one of' },
    [ConditionOperatorEnum.NotIn]: { label: 'is not one of' },
  },
  [PropertyPolicyRelation.SingleValueIncludeOnly]: {
    [ConditionOperatorEnum.In]: { label: 'is one of' },
  },
  [PropertyPolicyRelation.MultipleValues]: {
    [ConditionOperatorEnum.ContainsAnyOf]: { label: 'contains any of' },
    [ConditionOperatorEnum.ContainsAllOf]: { label: 'contains all of' },
    [ConditionOperatorEnum.ContainsNoneOf]: { label: 'contains none of' },
  },
  [PropertyPolicyRelation.String]: {
    [ConditionOperatorEnum.Contains]: { label: 'contains' },
    [ConditionOperatorEnum.NotContains]: { label: 'does not contain' },
    [ConditionOperatorEnum.StartsWith]: { label: 'starts with' },
    [ConditionOperatorEnum.NotStartsWith]: {
      label: 'does not start with',
    },
    [ConditionOperatorEnum.EndsWith]: { label: 'ends with' },
    [ConditionOperatorEnum.NotEndsWith]: { label: 'does not end with' },
  },
  [PropertyPolicyRelation.SingleValueOrString]: {
    [ConditionOperatorEnum.In]: { label: 'is one of' },
    [ConditionOperatorEnum.NotIn]: { label: 'is not one of' },
    [ConditionOperatorEnum.Contains]: { label: 'contains' },
    [ConditionOperatorEnum.NotContains]: { label: 'does not contain' },
    [ConditionOperatorEnum.StartsWith]: { label: 'starts with' },
    [ConditionOperatorEnum.NotStartsWith]: {
      label: 'does not start with',
    },
    [ConditionOperatorEnum.EndsWith]: { label: 'ends with' },
    [ConditionOperatorEnum.NotEndsWith]: { label: 'does not end with' },
  },
  [PropertyPolicyRelation.Numeric]: {
    [ConditionOperatorEnum.GreaterThan]: { label: 'is greater than' },
    [ConditionOperatorEnum.GreaterThanOrEquals]: {
      label: 'is greater than or equal to',
    },
    [ConditionOperatorEnum.LessThan]: { label: 'is less than' },
    [ConditionOperatorEnum.LessThanOrEquals]: {
      label: 'is less than or equal to',
    },
    [ConditionOperatorEnum.Equals]: { label: 'is' },
    [ConditionOperatorEnum.NotEquals]: { label: 'is not' },
  },
};

/**
 * The operator a condition opens on, per relation. Every relation lists it first
 * in {@link OPERATORS} too, but stating it here keeps the choice out of the hands
 * of object key order — and makes adding a relation a type error until its
 * default is named.
 */
const DEFAULT_OPERATORS: Record<PropertyPolicyRelation, ConditionOperatorEnum> =
  {
    [PropertyPolicyRelation.SingleValue]: ConditionOperatorEnum.In,
    [PropertyPolicyRelation.SingleValueOrString]: ConditionOperatorEnum.In,
    [PropertyPolicyRelation.SingleValueIncludeOnly]: ConditionOperatorEnum.In,
    [PropertyPolicyRelation.MultipleValues]:
      ConditionOperatorEnum.ContainsAnyOf,
    [PropertyPolicyRelation.String]: ConditionOperatorEnum.Contains,
    [PropertyPolicyRelation.Numeric]: ConditionOperatorEnum.GreaterThan,
  };

/** Single-select is the default relation, so a property that names none matches on exact values. */
export const relationOf = (property: FilterProperty) =>
  property.policyRelation ?? PropertyPolicyRelation.SingleValue;

export const defaultOperatorFor = (property: FilterProperty) =>
  DEFAULT_OPERATORS[relationOf(property)];

/**
 * On a multi-value property `Contains` means "contains all of", and conditions
 * stored before that meaning had an operator of its own still spell it that way.
 * Both spellings resolve to the same backend operator — `AsOperator` folds
 * "Contains All Of" into `Contains` — so this renames a condition without
 * changing what it matches.
 *
 * Worth rewriting rather than just relabelling: the two spellings serialize to
 * different filter fields, and multi-value schemas define only `containsAllOf`,
 * so a condition left spelled the old way is dropped by the API and its filter
 * reads as applied while the list stays unfiltered.
 */
export const canonicalOperator = (
  operator: string | undefined,
  relation: PropertyPolicyRelation
) =>
  relation === PropertyPolicyRelation.MultipleValues &&
  operator === ConditionOperatorEnum.Contains
    ? ConditionOperatorEnum.ContainsAllOf
    : (operator ?? '');

interface OperatorSelectProps {
  condition: Condition;
  propertyRelation: PropertyPolicyRelation;
  onChange: (condition: Condition) => void;
}

export function OperatorSelect({
  condition,
  propertyRelation,
  onChange,
}: OperatorSelectProps) {
  return (
    <Select
      sx={{ ...inlineDropdownStyle, height: '100%' }}
      MenuProps={{ disablePortal: true }}
      value={canonicalOperator(condition.operator, propertyRelation)}
      onChange={(event) =>
        onChange({
          ...condition,
          operator: event.target.value,
          // Every operator of a numeric relation compares the same threshold, so the
          // one already entered still means what it did. The value sets the other
          // relations pick from are operator-specific, and stale picks there survive
          // into a condition their new operator cannot express.
          value:
            propertyRelation === PropertyPolicyRelation.Numeric
              ? condition.value
              : [],
        })
      }
    >
      {Object.entries(OPERATORS[propertyRelation]).map(([key, value]) => (
        <MenuItem key={key} value={key}>
          {value.label}
        </MenuItem>
      ))}
    </Select>
  );
}
